import { Component, OnInit, Input, Inject, Output, EventEmitter, ViewChild, AfterViewInit, OnDestroy, LOCALE_ID } from '@angular/core';
import { AdminService } from '../services/admin.service';
import { GenericModelList } from '../models/list.summary.interface';
import { GenericPageTypes } from '../enums/generic-page-types.enum';
import { GenericPageInfo } from '../models/generic.pageinfo.interface';
import { MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { OfficeDialog } from '../office/office.dialog';

import { RoleComponent } from '../role/role.component';
import { VendorComponent } from '../vendor/vendor.component';
import { UserEditComponent } from '../../user/edit/edit.component';
import { GenericDialogData } from '../models/generic.dialog.interface';
import { EditComponent } from '../edit/edit.component';
import { CrewComponent } from '../crew/crew.component';
import { FieldTechnicianComponent } from '../field-technician/field.technician.component';
import { ItemTypeComponent } from '../item-type/item-type.component';
import { WarehouseComponent } from '../warehouse/warehouse.component';
import { ResourceComponent } from '../resource/resource.component';
import { QuoteAreaDialog } from '../../shared/controls/quote/quote-area/quote-area.dialog';
import { Subject, Subscription } from 'rxjs';
import { AuthService } from '../../shared/services/auth.service';
import { MatRadioChange } from '@angular/material/radio';
import { finalize } from 'rxjs/internal/operators/finalize';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { HttpErrorResponse } from '@angular/common/http';
import { DocumentComponent } from '../document/document.component';
import { FileService } from '../../shared/services/file.service';
import { EventService } from '../../shared/services/event.service';
import { EventType_Enum } from '../enums/event-type.enum';


@Component({
  selector: 'app-admin-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent implements OnInit, AfterViewInit, OnDestroy {
  dataSource = new MatTableDataSource<GenericModelList>();
  get displayedColumns(): string[] {
    switch (this.pageInfo.nextRoute.toLowerCase()) {
      case 'account':
      case 'crew':
      case 'vendor':
      case 'warehouse':
      case 'document':
        return ['name', 'additionalInfo', 'status'];
      case 'office':
        return ['name', 'booleanInfo', 'status'];
      default:
        return ['name', 'status'];
    }
  }

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private dealerSubscription: Subscription;

  dealerId: number;
  action: string;
  properties: string[];
  genericModelList: GenericModelList[];
  pageTypes: GenericPageTypes;
  pageInfo: GenericPageInfo;
  errors: string;
  primaryOffice: string;
  showActive: boolean = false;

  @Output() showQuoteTemplate: EventEmitter<any> = new EventEmitter<any>();

  constructor(private adminService: AdminService, private fileService: FileService, private eventService: EventService, @Inject(LOCALE_ID) private locale: string,
    public dialog: MatDialog, private snackBar: MatSnackBar) { }

  ngOnInit() {
    let isIDIAdmin = (AdminService._authNavStatusSource.value == 'IDI');

    this.dealerSubscription = AdminService.impersonatedDealer$.subscribe(dealerId => {
      if (isIDIAdmin) this.dealerId = dealerId;
      this.pageInfo = this.adminService.setGenericPageInfo(this.action, this.locale);
      this.loadData(this.action);
    });
  }

  // parent tab control on page layout require waiting until after view init
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  @Input()
  public set listType(value: string) {
    if (value) {
      this.action = value;
    }
  }

  get additionalInfoLabel(): string {
    switch (this.pageInfo.nextRoute.toLowerCase()) {
      case 'account':
        return $localize`Income/Expense`;
      case 'crew':
        return $localize`Leader`;
      case 'document':
      case 'quoteitemnotetemplate':
        return $localize`Description`;
      case 'vendor':
        return $localize`Name`;
      case 'warehouse':
        return $localize`Locations`;

      default:
        return $localize`Additional Info`;
    }
  }

  additionalInfoText(entity: GenericModelList): string {
    switch (this.pageInfo.nextRoute) {
      case 'warehouse':
        return `${entity.additionalInfo} Locations Available`;
      default:
        return entity.additionalInfo;
    }
  }

  toggleFilter(page: string) {
    this.showActive = !this.showActive;
    this.loadData(this.action);
  }

  toggleStatus(page: string, id) {
    let dealerId = this.adminService.getDealerId();

    this.adminService.toggleStatus(page, id, dealerId)
      .subscribe(result => {
        console.log(result);
        if (result.id > 0) {

          // for the itemtype... send an event so the subsribers can reload itemtypes
          if (page == "ItemType") {
            this.eventService.publish(EventType_Enum.ItemTypeModification)
          }

          //the template is opposite because it has an isdeleted vs isactive field
          if (page == "QuoteTemplate") {
            this.genericModelList.find(gl => gl.id == id).isActive = !result.isActive;
            this.setStatusText(!result.isActive, page, id)
          }
          else {
            this.genericModelList.find(gl => gl.id == id).isActive = result.isActive;
            this.setStatusText(result.isActive, page, id)
          }
        }
      });
  }

  toggleUserStatus(page: string, id: string) {
    this.errors = '';
    let dealerId = this.adminService.getDealerId();

    this.adminService.toggleUserStatus(page, id, dealerId)
      .subscribe({
        next: (result) => {
          if (result.id != "") {
            this.genericModelList.find(gl => gl.id == id).isActive = result.isActive;
            this.setStatusText(result.isActive, page, id);
          }
        },
        error: (error: string) => {
          this.errors = error;
          console.log(this.errors);
          // on an error we need to put the switch back to the original position
          let statusId = "status_" + id
          let userStatus = (<HTMLInputElement>document.getElementById(statusId)).checked;
          (<HTMLInputElement>document.getElementById(statusId)).checked = !userStatus;
          this.snackBar.open(this.errors, null, { duration: 4000, verticalPosition: 'bottom', panelClass: ['bidit-snackbar-alert'] })
        }
      });
  }

  setStatusText(isActive: boolean, page: string, id: any) {
    let activeStatusId = page + "_status_active" + id;
    let inActiveStatusId = page + "_status_inactive" + id;

    if (isActive) {
      document.getElementById(activeStatusId).className = "text-success";
      document.getElementById(inActiveStatusId).className = "";
    }
    else {
      document.getElementById(activeStatusId).className = "";
      document.getElementById(inActiveStatusId).className = "text-danger";
    }
  }

  primaryOfficeChanged(event: MatRadioChange) {
    this.adminService.setPrimaryOffice(event.value, this.dealerId)
      .subscribe(
        result => {
          if (result) {
            this.loadData(this.action);
          }
        })
    //error: (error: string) =>this.errors = error);
  }


  showEdit(page: string, id): void {

    if (page == 'QuoteTemplate') {
      this.editTemplate(id);
      return;
    }

    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = { id: id, action: page } as GenericDialogData;
    dialogConfig.width = '800px';

    var dialogToOpen;

    switch (page.toLowerCase()) {
      case GenericPageTypes.Area.toLowerCase():
        dialogToOpen = QuoteAreaDialog;
        break;
      case GenericPageTypes.Crew.toLowerCase():
        dialogToOpen = CrewComponent;
        break;
      case GenericPageTypes.Document.toLowerCase():
        dialogToOpen = DocumentComponent;
        break;
      case GenericPageTypes["Field Technician"].replace(" ", "").toLowerCase():
        dialogToOpen = FieldTechnicianComponent;
        break;
      case GenericPageTypes["Item Type"].replace(" ", "").toLowerCase():
        dialogToOpen = ItemTypeComponent;
        break;
      case GenericPageTypes.Office.toLocaleLowerCase():
        dialogToOpen = OfficeDialog;
        dialogConfig.width = '900px';
        break;
      case GenericPageTypes.Resource.toLocaleLowerCase():
        dialogToOpen = ResourceComponent;
        break;
      case GenericPageTypes.Role.toLocaleLowerCase():
        dialogToOpen = RoleComponent;
        break;
      case GenericPageTypes.User.toLocaleLowerCase():
        dialogToOpen = UserEditComponent;
        break;
      case GenericPageTypes.Vendor.toLocaleLowerCase():
        dialogToOpen = VendorComponent;
        dialogConfig.width = '1000px';
        break;
      case GenericPageTypes.Warehouse.toLocaleLowerCase():
        dialogToOpen = WarehouseComponent;
        dialogConfig.width = '900px';
        break;
      default:
        dialogToOpen = EditComponent;
    }

    const dialogRef = this.dialog.open(dialogToOpen, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
      this.loadData(this.action);
    });
  }

  loadData(page: string) {
    this.adminService.getList(page, this.dealerId)
      .subscribe({
        next: (data) => {
          const filtered = data.filter(d => d.isActive || this.showActive);

          this.genericModelList = filtered;
          if (page == GenericPageTypes.Office.toLocaleLowerCase()) {
            this.primaryOffice = this.genericModelList.find(gml => gml.booleanInfo == true)?.id;
          }

          this.dataSource.data = filtered;
        }
      });
  }

  editTemplate(id: any) {
    this.showQuoteTemplate.emit(id);
  }

  downloadAttachment(entity: GenericModelList) {
    this.adminService.getDocumentAttachment(+entity.id)
      .subscribe({
        next: (data) => {
          this.fileService.downloadFile(data, entity.name);
        }
      });
  }

  ngOnDestroy(): void {
    this.dealerSubscription.unsubscribe();
  }
}
